import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "@/components/Button";
import Autocomplete from "@/components/Autocomplete";
import Select from "@/components/Select";
import Dialog from "@/components/Dialog";
import AvatarDetails from "@/components/AvatarDetails";
import { useSomePeopleQuery } from "@/pages/people/_hooks/useSomePeopleQuery";
import classNames from "classnames";
import Banner from "@/components/Banner";

interface AddPersonModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (personId: string, role: string) => void;
  isLoading?: boolean;
}

interface FormValues {
  personId: string;
  role: string;
}

interface PersonData {
  id: string;
  firstName: string;
  lastName: string;
  pgaId: string;
  email?: string;
  profilePhoto?: string;
  primaryFacility?: {
    name: string;
  };
  primarySectionAffiliation?: {
    section: {
      name: string;
    };
  };
}

const ROLE_OPTIONS = [
  {
    value: "Manage Jobs",
    label: "Manage Jobs",
    description:
      'People with "Manage Jobs" role can post jobs on the pga.org job board, as well as close and view job applicants for jobs where they are the employer.'
  },
  {
    value: "View Applicants",
    label: "View Applicants",
    description: 'People with "View Applicants" role can view job applicants for any job at this facility.'
  },
  {
    value: "Close Jobs",
    label: "Close Jobs",
    description: 'People with "Close Jobs" role can close any job at this facility on pga.org.'
  }
];

export function AddPersonModal({ open, onClose, onSave, isLoading = false }: AddPersonModalProps) {
  const [personSearch, setPersonSearch] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [peopleVariables, setPeopleVariables] = useState<{ search?: string }>({});

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      personId: "",
      role: "Manage Jobs"
    }
  });

  const selectedRole = watch("role");

  const { data: peopleData } = useSomePeopleQuery({
    variables: { input: peopleVariables, first: 10 },
    skip: Object.keys(peopleVariables).length === 0 || peopleVariables.search === ""
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setPeopleVariables({ search: debouncedSearchTerm });
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearchTerm]);

  const onSubmit = (values: FormValues) => {
    if (values.personId && values.role) {
      onSave(values.personId, values.role);
      // Don't close the modal here - let the parent component handle it based on mutation response
    }
  };

  const handleClose = () => {
    reset();
    setPersonSearch("");
    setDebouncedSearchTerm("");
    setPeopleVariables({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Person"
      className="sm:max-w-[480px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
            icon={isLoading ? "progress_activity" : undefined}
            iconClassName={classNames(isLoading && "animate-spin w-6 h-6 !block")}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-on-surface">
          Select a person to add to this facility&apos;s permissions.
        </p>

        <>
          <Controller
            name="personId"
            control={control}
            rules={{ required: "Person is required" }}
            render={({ fieldState }) => (
              <Autocomplete
                autofocus
                label="Person"
                data={(peopleData?.people?.nodes || []) as PersonData[]}
                onChangeText={(string) => {
                  setPersonSearch(string);
                  setDebouncedSearchTerm(string);

                  if (string === "") {
                    setValue("personId", "");
                  }
                }}
                query={personSearch}
                onSelect={(person: PersonData) => {
                  setValue("personId", person.id);
                  setPersonSearch(`${person.firstName} ${person.lastName}`);
                }}
                reset={() => {
                  setPersonSearch("");
                  setPeopleVariables({ search: "" });
                  setValue("personId", "");
                }}
                renderItem={(data: PersonData) => {
                  return (
                    <div className="py-1 w-full">
                      <AvatarDetails
                        title={`${data.firstName} ${data.lastName}`}
                        text={
                          <div className="flex flex-col">
                            {data.primaryFacility?.name && (
                              <div className="text-body-small text-sys-brand-on-surface-variant">
                                {data.primaryFacility.name}
                              </div>
                            )}
                            {data.email && (
                              <div className="text-body-small text-sys-brand-on-surface-variant">{data.email}</div>
                            )}
                          </div>
                        }
                        picture={data.profilePhoto}
                        secondaryText={data.primarySectionAffiliation?.section?.name || ""}
                      />
                    </div>
                  );
                }}
                variant="searchableDropdown"
                error={fieldState.invalid}
                errorText={fieldState.error?.message}
                required
                placeholder="Search for a person..."
                leadingIcon="search"
                menuClassName="max-h-[300px]"
                constrainMenuWidth={true}
              />
            )}
          />
        </>

        <Controller
          name="role"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <Select
              label="Role"
              options={ROLE_OPTIONS}
              value={value}
              onSelect={onChange}
              error={fieldState.invalid}
              errorText={fieldState.error?.message}
              required
              placeholder="Select a role"
            />
          )}
        />

        {/* Role Description */}
        {selectedRole && (
          <Banner
            variant="info"
            inline
            description={ROLE_OPTIONS.find((role) => role.value === selectedRole)?.description || ""}
          />
        )}
      </div>
    </Dialog>
  );
}
